import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  IconButton,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormNavigationProvider, NavTextField } from '../components/FormNavigation';
import {
  Add,
  Delete,
  Edit,
  FileDownload,
  FileUpload,
  NavigateNext,
  Save,
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { showNotification } from '../store/notificationSlice';
import { useAppDispatch, useAppSelector } from '../store';
import { addLeaveType, updateLeaveType as updateLeaveTypeInStore, removeLeaveType } from '../store/leaveTypesSlice';
import { apiService } from '../services/api';
import type { LeaveType } from '../types/leave';

interface ImportCandidate {
  id?: string;
  name: string;
  isActive: boolean;
}

interface ExistingItem {
  id: string;
  name: string;
  reason: string;
}

interface FailedItem {
  id: string;
  name: string;
  error: string;
}

const LeaveTypes: React.FC = () => {
  const dispatch = useAppDispatch();
  const leaveTypes = useAppSelector((s) => s.leaveTypes.leaveTypes);

  const [editTarget, setEditTarget] = useState<LeaveType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeaveType | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Import / Export state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [candidatesToImport, setCandidatesToImport] = useState<ImportCandidate[]>([]);
  const [alreadyExistingItems, setAlreadyExistingItems] = useState<ExistingItem[]>([]);
  const [importedItems, setImportedItems] = useState<Array<{ id: string; name: string }>>([]);
  const [failedItems, setFailedItems] = useState<FailedItem[]>([]);
  const [importStatus, setImportStatus] = useState<'preview' | 'importing' | 'completed'>('preview');

  const handleExport = () => {
    if (leaveTypes.length === 0) {
      dispatch(showNotification({ message: 'No leave types to export', severity: 'warning' }));
      return;
    }

    const exportData = leaveTypes.map((lt) => ({
      id: lt.id,
      name: lt.name,
      description: lt.name,
      isActive: lt.isActive,
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leave-types-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    dispatch(
      showNotification({
        message: `Exported ${exportData.length} leave type(s) successfully`,
        severity: 'success',
      })
    );
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text);
        const list: any[] = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.leaveTypes)
          ? parsed.leaveTypes
          : Array.isArray(parsed?.data)
          ? parsed.data
          : null;

        if (!list || list.length === 0) {
          dispatch(
            showNotification({
              message: 'Invalid JSON file: Expected a JSON array of leave types.',
              severity: 'error',
            })
          );
          return;
        }

        const existingById = new Map(leaveTypes.map((lt) => [lt.id.toLowerCase(), lt]));
        const existingByName = new Map(leaveTypes.map((lt) => [lt.name.trim().toLowerCase(), lt]));

        const toImport: ImportCandidate[] = [];
        const existing: ExistingItem[] = [];

        for (const raw of list) {
          if (!raw || typeof raw !== 'object') continue;
          const rawId = String(raw.id || raw.uuid || '').trim();
          const rawName = String(raw.name || raw.description || '').trim();
          const isActive = raw.isActive !== false;

          if (!rawName) continue;

          if (rawId && existingById.has(rawId.toLowerCase())) {
            const match = existingById.get(rawId.toLowerCase())!;
            existing.push({
              id: rawId,
              name: rawName,
              reason: `UUID already exists (${match.name})`,
            });
            continue;
          }

          if (existingByName.has(rawName.toLowerCase())) {
            const match = existingByName.get(rawName.toLowerCase())!;
            existing.push({
              id: rawId || match.id,
              name: rawName,
              reason: `Leave type with name "${rawName}" already exists`,
            });
            continue;
          }

          if (toImport.some((c) => (rawId && c.id && c.id.toLowerCase() === rawId.toLowerCase()) || c.name.toLowerCase() === rawName.toLowerCase())) {
            existing.push({
              id: rawId || 'N/A',
              name: rawName,
              reason: 'Duplicate entry in imported file',
            });
            continue;
          }

          toImport.push({
            id: rawId || undefined,
            name: rawName,
            isActive,
          });
        }

        setCandidatesToImport(toImport);
        setAlreadyExistingItems(existing);
        setImportedItems([]);
        setFailedItems([]);
        setImportStatus('preview');
        setImportDialogOpen(true);
      } catch (err: any) {
        dispatch(
          showNotification({
            message: 'Failed to parse JSON file: ' + (err?.message || 'Invalid JSON syntax'),
            severity: 'error',
          })
        );
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const handleExecuteImport = async () => {
    setImportStatus('importing');
    const successfullyImported: Array<{ id: string; name: string }> = [];
    const updatedAlreadyExisting: ExistingItem[] = [...alreadyExistingItems];
    const failed: FailedItem[] = [];

    for (const item of candidatesToImport) {
      try {
        const res = await apiService.createLeaveType({
          id: item.id,
          name: item.name,
          isActive: item.isActive,
        });
        dispatch(addLeaveType(res.data));
        successfullyImported.push({ id: res.data.id, name: res.data.name });
      } catch (err: any) {
        const errorMsg = err?.message || '';
        const isConflict =
          err?.status === 409 ||
          err?.status === 422 ||
          errorMsg.toLowerCase().includes('already exists') ||
          (err?.jsonMessage && typeof err.jsonMessage === 'object' && Object.values(err.jsonMessage).some((v: any) => String(v).toLowerCase().includes('already exists')));

        if (isConflict) {
          updatedAlreadyExisting.push({
            id: item.id || 'N/A',
            name: item.name,
            reason: errorMsg || 'Already exists in database',
          });
        } else {
          failed.push({
            id: item.id || 'N/A',
            name: item.name,
            error: errorMsg || 'Failed to create',
          });
        }
      }
    }

    setImportedItems(successfullyImported);
    setAlreadyExistingItems(updatedAlreadyExisting);
    setFailedItems(failed);
    setImportStatus('completed');

    if (successfullyImported.length > 0) {
      dispatch(
        showNotification({
          message: `Successfully imported ${successfullyImported.length} leave type(s)`,
          severity: 'success',
        })
      );
    } else if (updatedAlreadyExisting.length > 0) {
      dispatch(
        showNotification({
          message: `Import finished. All ${updatedAlreadyExisting.length} leave type(s) already exist`,
          severity: 'info',
        })
      );
    }
  };

  return (
    <DashboardLayout>
      <Box>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Leave Management</Typography>
          <Typography variant="body2" color="text.primary" fontWeight={600}>Leave Types</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Leave Types</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <input
            type="file"
            ref={fileInputRef}
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <Button
            variant="outlined"
            startIcon={<FileUpload />}
            onClick={() => fileInputRef.current?.click()}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Create Leave Type
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaveTypes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No leave types found. Create your first leave type to get started.
                  </TableCell>
                </TableRow>
              )}
              {leaveTypes.map((lt) => (
                <TableRow key={lt.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{lt.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lt.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={lt.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(lt.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditTarget(lt)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(lt)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <CreateLeaveTypeDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(lt) => {
            dispatch(addLeaveType(lt));
            dispatch(showNotification({ message: 'Leave type created', severity: 'success' }));
          }}
        />

        {editTarget && (
          <EditLeaveTypeDialog
            open
            leaveType={editTarget}
            onClose={() => setEditTarget(null)}
            onUpdated={(updated) => {
              dispatch(updateLeaveTypeInStore(updated));
              setEditTarget(null);
              dispatch(showNotification({ message: 'Leave type updated', severity: 'success' }));
            }}
          />
        )}

        {deleteTarget && (
          <DeleteLeaveTypeDialog
            open
            leaveType={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onDeleted={(id) => {
              dispatch(removeLeaveType(id));
              setDeleteTarget(null);
              dispatch(showNotification({ message: 'Leave type deleted', severity: 'success' }));
            }}
          />
        )}

        <ImportLeaveTypesDialog
          open={importDialogOpen}
          fileName={importFileName}
          candidates={candidatesToImport}
          existing={alreadyExistingItems}
          imported={importedItems}
          failed={failedItems}
          status={importStatus}
          onClose={() => setImportDialogOpen(false)}
          onImport={handleExecuteImport}
        />
      </Box>
    </DashboardLayout>
  );
};

// ─── Create Dialog ──────────────────────────────────────────────────────────

interface CreateLeaveTypeDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (lt: LeaveType) => void;
}

const CreateLeaveTypeDialog: React.FC<CreateLeaveTypeDialogProps> = ({ open, onClose, onCreated }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setIsActive(true);
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setErrors({ name: 'Name is required.' });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const res = await apiService.createLeaveType({ name: name.trim(), isActive });
      onCreated(res.data);
      onClose();
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to create leave type', severity: 'error' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Create Leave Type</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <NavTextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              error={!!errors['name']}
              helperText={errors['name']}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Active</Typography>
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Save />}>
            Create
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
};

// ─── Edit Dialog ────────────────────────────────────────────────────────────

interface EditLeaveTypeDialogProps {
  open: boolean;
  leaveType: LeaveType;
  onClose: () => void;
  onUpdated: (lt: LeaveType) => void;
}

const EditLeaveTypeDialog: React.FC<EditLeaveTypeDialogProps> = ({ open, leaveType, onClose, onUpdated }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(leaveType.name);
  const [isActive, setIsActive] = useState(leaveType.isActive);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setName(leaveType.name);
      setIsActive(leaveType.isActive);
      setErrors({});
    }
  }, [open, leaveType]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setErrors({ name: 'Name is required.' });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const res = await apiService.updateLeaveType(leaveType.id, { name: name.trim(), isActive });
      onUpdated(res.data);
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to update leave type', severity: 'error' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Edit Leave Type</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <NavTextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              error={!!errors['name']}
              helperText={errors['name']}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Active</Typography>
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Save />}>
            Save
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
};

// ─── Delete Dialog ──────────────────────────────────────────────────────────

interface DeleteLeaveTypeDialogProps {
  open: boolean;
  leaveType: LeaveType;
  onClose: () => void;
  onDeleted: (id: string) => void;
}

const DeleteLeaveTypeDialog: React.FC<DeleteLeaveTypeDialogProps> = ({ open, leaveType, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await apiService.deleteLeaveType(leaveType.id);
      onDeleted(leaveType.id);
    } catch (err: any) {
      setError(err.message ?? 'Failed to delete leave type');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Leave Type</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography>
          Delete <strong>{leaveType.name}</strong>? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Delete />}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Import Dialog ──────────────────────────────────────────────────────────

interface ImportLeaveTypesDialogProps {
  open: boolean;
  fileName: string;
  candidates: ImportCandidate[];
  existing: ExistingItem[];
  imported: Array<{ id: string; name: string }>;
  failed: FailedItem[];
  status: 'preview' | 'importing' | 'completed';
  onClose: () => void;
  onImport: () => void;
}

const ImportLeaveTypesDialog: React.FC<ImportLeaveTypesDialogProps> = ({
  open,
  fileName,
  candidates,
  existing,
  imported,
  failed,
  status,
  onClose,
  onImport,
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => status !== 'importing' && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileUpload />
          <span>Import Leave Types</span>
        </Box>
        {fileName && (
          <Typography variant="caption" color="text.secondary">
            {fileName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {status === 'preview' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Total in File: ${candidates.length + existing.length}`} size="small" />
              <Chip
                label={`Ready to Import: ${candidates.length}`}
                size="small"
                color={candidates.length > 0 ? 'success' : 'default'}
              />
              <Chip
                label={`Already Exists: ${existing.length}`}
                size="small"
                color={existing.length > 0 ? 'warning' : 'default'}
              />
            </Box>

            {existing.length > 0 && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  The following {existing.length} leave type(s) already exist in the system and will be skipped:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 160 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name / Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UUID / ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existing.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{item.id}</TableCell>
                          <TableCell>
                            <Chip label={item.reason} size="small" color="warning" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {candidates.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  New Leave Types to Import ({candidates.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name / Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UUID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {candidates.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {item.id || 'Auto-generated'}
                          </TableCell>
                          <TableCell>
                            <Chip label="Ready to Import" size="small" color="success" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info">
                All leave types in this file already exist in the system. There are no new items to import.
              </Alert>
            )}
          </Box>
        )}

        {status === 'importing' && (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} />
            <Typography variant="body1">Importing leave types, please wait...</Typography>
            <LinearProgress sx={{ width: '80%' }} />
          </Box>
        )}

        {status === 'completed' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {imported.length > 0 && (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={600}>
                  Successfully imported {imported.length} leave type(s):
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 160 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name / Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UUID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {imported.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{item.id}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {existing.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight={600}>
                  The following {existing.length} leave type(s) already existed and were skipped:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 160 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name / Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UUID / ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existing.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{item.id}</TableCell>
                          <TableCell>
                            <Chip label={item.reason} size="small" color="warning" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {failed.length > 0 && (
              <Alert severity="error">
                <Typography variant="body2" fontWeight={600}>
                  {failed.length} leave type(s) failed to import:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 140 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name / Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failed.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell>{item.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {status === 'preview' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onImport}
              disabled={candidates.length === 0}
              startIcon={<FileUpload />}
            >
              Import {candidates.length > 0 ? `(${candidates.length})` : ''}
            </Button>
          </>
        )}
        {status === 'importing' && (
          <Button disabled>Importing...</Button>
        )}
        {status === 'completed' && (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LeaveTypes;
